import axios from 'axios';

// Configure axios instance with base URL
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const LIGHTER_API_BASE_URL = process.env.REACT_APP_LIGHTER_API_BASE_URL || 'https://mainnet.zklighter.elliot.ai';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add Telegram user data if available
api.interceptors.request.use((config) => {
  // Get Telegram WebApp data
  if (window.Telegram?.WebApp) {
    const initData = window.Telegram.WebApp.initData;
    if (initData) {
      config.headers['X-Telegram-Init-Data'] = initData;
    }
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error);
    return Promise.reject(error);
  }
);

export const apiService = {
  // Validate Telegram user
  validateUser: async (userData) => {
    try {
      const response = await api.post('/api/validateUser', userData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get user details
  getUser: async (userId) => {
    try {
      const response = await api.get(`/api/getUser/${userId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get user duel details
  getDuel: async (duelId) => {
    try {
      const response = await api.get(`/api/getDuel/${duelId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get all open duels
  getOpenDuels: async () => {
    try {
      const response = await api.get(`/api/getOpenDuels`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get user last duel
  getUserLastDuel: async (userId) => {
    try {
      const response = await api.get(`/api/getUserLastDuel/${userId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get trade details
  getTradeDetails: async (challengerUserId, opponentUserId, duelId) => {
    try {
      const response = await api.get(`/api/tradeDetails?challenger_user_id=${challengerUserId}&opponent_user_id=${opponentUserId}&duelId=${duelId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get market details
  getMarketDetails: async () => {
    try {
      const response = await axios.get('https://storage.googleapis.com/lumos-cdn/files/markets.json');
      const markets = response.data;
      return markets;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Place a bet on a duel
  duelBet: async (betData) => {
    try {
      const response = await api.post(`/api/duelBet`, betData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get all bets for a duel
  getDuelBets: async (duelId) => {
    try {
      const response = await api.get(`/api/getDuelBets?duel_id=${duelId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get candlestick data for a market
  getCandlesticks: async (marketId, resolution = 60, countBack = 100, setTimestampToEnd = false) => {
    try {
      // Resolution mapping for string values
      const resolutionMap = {
        "1m": 60,
        "5m": 300,
        "15m": 900,
        "1h": 3600,
        "4h": 14400,
        "1d": 86400
      };

      // Convert resolution to appropriate format
      let resolutionInt;
      let resolutionParam;
      if (typeof resolution === 'string' && resolution in resolutionMap) {
        resolutionInt = resolutionMap[resolution];
        resolutionParam = resolution;  // Use string for API
      } else {
        resolutionInt = parseInt(resolution);
        resolutionParam = resolutionInt;  // Use int for API
      }

      // Calculate timestamps
      const endTimestamp = Math.floor(Date.now() / 1000);  // Current time in seconds
      const startTimestamp = endTimestamp - (resolutionInt * countBack);

      // Build query parameters
      const params = {
        market_id: marketId,
        resolution: resolutionParam,
        start_timestamp: startTimestamp * 1000,  // Convert to milliseconds
        end_timestamp: endTimestamp * 1000,      // Convert to milliseconds
        count_back: Math.min(countBack, 1000)    // API max limit is 1000
      };

      const response = await axios.get(`${LIGHTER_API_BASE_URL}/api/v1/candlesticks`, {
        params,
        headers: { 'Content-Type': 'application/json' },
        timeout: 10000
      });
      return response.data?.candlesticks || [];
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Generic GET request
  get: async (endpoint, params = {}) => {
    try {
      const response = await api.get(endpoint, { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Generic POST request
  post: async (endpoint, data = {}) => {
    try {
      const response = await api.post(endpoint, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }
};

export default apiService;
